import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react'
import { Check, Zap } from 'lucide-react'
import cn from 'classnames'
import api from 'api/api'
import { downloadWalletBackup } from 'lib/walletBackup'
import { isRunningInExpoWebView } from 'utils/nativeShare'
import {
    claimWelcomeGift,
    loadLocalWalletStatus,
    loadLocalWalletStatusPatiently,
    recordWalletFileSaved,
    retryNetworkFailure,
    UNTIL_DOWNLOAD_PROMPT_CLOSES,
    walletSetupRemaining,
    type LocalWalletStatus,
} from 'lib/localWallet'
import {
    advanceWalletPasskeyStep,
    discardPendingWalletPasskey,
    loadWalletPasskeyStatus,
    passkeysUsableOnThisDevice,
    prepareWalletPasskeyAction,
    prepareWalletPasskeyEnrollment,
    type WalletPasskeyStep,
} from 'lib/walletPasskeyRecovery'
import {
    passkeyBrowserUnavailableReason,
    walletPasskeyErrorMessage,
} from 'lib/walletPasskeyWebAuthn'
import styles from './walletSetup.module.scss'

// Download the wallet file (first half of the gift), then create a passkey and lock
// the wallet with it, which also verifies it (second half), then the received screen. Checking the saved file is a
// separate reward offered afterwards (WalletFileCheck). Every screen has the same
// shape: "1/3 · DOWNLOAD WALLET" on top, one line of text, one big button with its
// reward right under it.
type Stage = 'file' | 'passkey' | 'gift'

const STAGE_NAMES: Record<Stage, string> = {
    file: 'Download wallet',
    passkey: 'Add passkey',
    gift: 'Done',
}
type PasskeyKind = 'create' | 'encrypt' | 'verify'

const GIFT_POLL_MS = 3000
const GIFT_POLL_LIMIT = 60

export function formatSats(sats: number): string {
    return sats.toLocaleString('en-US')
}

export function compactSats(sats: number): string {
    if (sats >= 1000 && sats % 1000 === 0) return `${sats / 1000}K`
    return formatSats(sats)
}

function stageFor(status: LocalWalletStatus, passkeysAvailable: boolean): Stage {
    if (!status.backup_file_saved) return 'file'
    if (passkeysAvailable && !status.passkey_verified) return 'passkey'
    return 'gift'
}

// Your phone asks three times; each prompt gets its own short screen. The verb leads
// each title and is drawn in orange.
const PASSKEY_COPY: Record<PasskeyKind, { verb: string; title: string; cta: string }> = {
    // The first prompt is written out in the screen itself.
    create: { verb: 'Create', title: '', cta: 'Create passkey' },
    encrypt: { verb: 'Lock', title: 'your wallet with passkey.', cta: 'Lock wallet' },
    verify: { verb: 'Verify', title: 'your passkey.', cta: 'Verify passkey' },
}

// The next passkey prompt: verify a backed-up passkey, lock the wallet with a created
// one, or create the first one.
async function nextPasskeyStep(): Promise<WalletPasskeyStep> {
    const passkeyStatus = await loadWalletPasskeyStatus()
    const pending = passkeysUsableOnThisDevice(passkeyStatus.passkeys).filter(p => !p.verified)
    const withBackup = pending.find(p => p.envelope_present)
    const created = pending.find(p => !p.envelope_present)
    let step: WalletPasskeyStep
    if (withBackup) {
        step = await prepareWalletPasskeyAction(withBackup, passkeyStatus.identity, 'verify')
    } else if (created) {
        step = await prepareWalletPasskeyAction(created, passkeyStatus.identity, 'encrypt')
    } else {
        step = await prepareWalletPasskeyEnrollment(passkeyStatus.root_identity_key)
    }
    return step
}

type StepToast = { id: number; text: string }

// "✓ Wallet saved" between steps: drops in at the top, then rises away.
const StepToastContext = createContext<{
    toast: StepToast | null
    showToast: (text: string) => void
}>({ toast: null, showToast: () => {} })

const STEP_TOAST_MS = 2200

type WalletSetupProps = React.ComponentProps<typeof WalletSetupScreens>

export default function WalletSetup(props: WalletSetupProps) {
    const [toast, setToast] = useState<StepToast | null>(null)
    const timer = useRef<ReturnType<typeof setTimeout>>()
    const showToast = useCallback((text: string) => {
        clearTimeout(timer.current)
        setToast({ id: Date.now(), text })
        timer.current = setTimeout(() => setToast(null), STEP_TOAST_MS)
    }, [])
    useEffect(() => () => clearTimeout(timer.current), [])
    return (
        <StepToastContext.Provider value={{ toast, showToast }}>
            <WalletSetupScreens {...props} />
        </StepToastContext.Provider>
    )
}

function WalletSetupScreens({
    userId,
    status,
    setupError,
    onRetrySetup,
    onStatus,
    onClose,
}: {
    userId: string
    status: LocalWalletStatus | null
    setupError: string
    onRetrySetup: () => void
    onStatus: (status: LocalWalletStatus) => void
    onClose: (options: { askForHelloPost: boolean; introduce?: boolean }) => void
}) {
    const passkeysAvailable = !passkeyBrowserUnavailableReason()
    const { showToast } = useContext(StepToastContext)
    const [skipping, setSkipping] = useState(false)
    const [busy, setBusy] = useState(false)
    const [error, setError] = useState('')
    const [giftError, setGiftError] = useState('')
    const [passkeyStep, setPasskeyStep] = useState<WalletPasskeyStep | null>(null)
    const passkeyStepRef = useRef<WalletPasskeyStep | null>(null)
    const [giftAttempt, setGiftAttempt] = useState(0)
    const followingGifts = useRef(false)
    // A step verified while an earlier payment is still being followed claims right after.
    const claimRequested = useRef(false)
    const statusRef = useRef(status)
    statusRef.current = status

    const stage = status ? stageFor(status, passkeysAvailable) : null
    const stepSats = {
        file: status?.gift_step_sats?.download ?? 0,
        passkey: passkeysAvailable ? status?.gift_step_sats?.passkey ?? 0 : 0,
    }
    const totalSats = stepSats.file + stepSats.passkey
    const passkeyKind: PasskeyKind = (passkeyStep?.kind as PasskeyKind) || 'create'
    const remaining = status ? walletSetupRemaining(status, passkeysAvailable) : null
    // The signup's own gifts; the later file check is shown on its own screen.
    const signupGifts = (status?.gifts || []).filter(gift => gift.step !== 'file_check')
    const receivedSats = signupGifts
        .filter(gift => gift.status === 'sent')
        .reduce((total, gift) => total + gift.amount_sats, 0)

    const rememberPasskeyStep = (step: WalletPasskeyStep | null) => {
        passkeyStepRef.current = step
        setPasskeyStep(step)
    }

    // Network work for the next passkey prompt happens before the tap: Safari only
    // shows the passkey sheet when it opens straight from the click.
    const preparePasskey = useCallback(async () => {
        rememberPasskeyStep(null)
        // This runs as the wallet file download starts, and iOS Safari cancels the
        // requests in flight at that moment ("Load failed"): ask again after a pause.
        try {
            rememberPasskeyStep(
                await retryNetworkFailure(nextPasskeyStep, UNTIL_DOWNLOAD_PROMPT_CLOSES),
            )
        } catch (caught) {
            setError(
                caught instanceof TypeError
                    ? 'Couldn’t reach Treechat. Check your connection and try again.'
                    : walletPasskeyErrorMessage(caught),
            )
        }
    }, [])

    const retryPreparePasskey = () => {
        setError('')
        void preparePasskey()
    }

    useEffect(() => {
        if (stage === 'passkey' && !passkeyStepRef.current) void preparePasskey()
    }, [preparePasskey, stage])

    const refreshStatus = useCallback(async () => {
        const next = await loadLocalWalletStatus()
        onStatus(next)
        return next
    }, [onStatus])

    // Each verified step earns its share: claim it, then follow the payments until
    // they land. A pending gift is claimed again (the server restarts a payment that
    // never started).
    const claimAndFollow = useCallback(async () => {
        claimRequested.current = true
        if (followingGifts.current) return
        followingGifts.current = true
        try {
            let rounds = 0
            while (claimRequested.current && rounds < 3) {
                claimRequested.current = false
                rounds += 1
                let next = await claimWelcomeGift()
                onStatus(next)
                let polls = 0
                while (
                    next.gifts.some(gift => ['pending', 'sending'].includes(gift.status)) &&
                    polls < GIFT_POLL_LIMIT
                ) {
                    polls += 1
                    await new Promise(resolve => setTimeout(resolve, GIFT_POLL_MS))
                    next = await loadLocalWalletStatusPatiently()
                    onStatus(next)
                }
                if (next.gifts.some(gift => gift.status === 'sent')) {
                    void api.getBsvBalance().catch(() => {})
                }
            }
        } catch (caught) {
            // Shown on the Done screen only: a gift problem isn't about the step on screen.
            setGiftError(
                caught instanceof TypeError
                    ? 'Couldn’t reach Treechat to send your sats.'
                    : caught instanceof Error
                    ? caught.message
                    : String(caught),
            )
        } finally {
            followingGifts.current = false
        }
    }, [onStatus])

    useEffect(() => {
        const current = statusRef.current
        if (!current || totalSats <= 0) return
        const giftOnItsWay = current.gifts.some(gift =>
            ['pending', 'sending'].includes(gift.status),
        )
        // A gift already on its way is followed too: on a retry (its status check failed,
        // not the claim) and when the setup reopens while it is still being sent.
        if (walletSetupRemaining(current, passkeysAvailable).giftUnclaimed || giftOnItsWay)
            void claimAndFollow()
        // Re-run when a step is earned or on a retry.
    }, [
        claimAndFollow,
        giftAttempt,
        passkeysAvailable,
        status?.backup_file_saved,
        status?.backup_file_verified,
        status?.passkey_verified,
        totalSats,
    ])

    const run = async (action: () => Promise<void>) => {
        if (busy) return
        setBusy(true)
        setError('')
        try {
            await action()
        } catch (caught) {
            setError(
                caught instanceof Error && caught.name === 'NotAllowedError'
                    ? walletPasskeyErrorMessage(caught)
                    : caught instanceof Error
                    ? caught.message
                    : String(caught),
            )
        } finally {
            setBusy(false)
        }
    }

    const downloadFile = () =>
        run(async () => {
            // In a browser the save is recorded before the download starts: iOS Safari
            // cancels requests in flight at that moment. The new status (which starts
            // the gift claim) is applied once the download is under way.
            let saved: LocalWalletStatus | null = null
            try {
                await downloadWalletBackup('client_shares', userId, {
                    beforeBrowserDownload: async () => {
                        saved = await recordWalletFileSaved()
                    },
                })
            } catch (caught) {
                if (isRunningInExpoWebView()) {
                    throw new Error('Update the Treechat app to save your wallet file.')
                }
                throw caught
            }
            onStatus(saved ?? (await recordWalletFileSaved()))
        })

    // Only an unfinished passkey can be discarded; the server refuses a verified one.
    const startNewPasskey = async () => {
        const step = passkeyStepRef.current
        if (!step || step.kind === 'create' || busy) return
        setBusy(true)
        try {
            await discardPendingWalletPasskey(step.passkey.id)
            setError('')
            await preparePasskey()
        } catch (caught) {
            setError(walletPasskeyErrorMessage(caught))
        } finally {
            setBusy(false)
        }
    }

    const advancePasskey = () => {
        const step = passkeyStepRef.current
        if (!step || busy) return
        setBusy(true)
        setError('')
        // No await before this call: it opens the passkey sheet inside the tap.
        advanceWalletPasskeyStep(step)
            .then(async result => {
                showToast(
                    step.kind === 'create'
                        ? 'Passkey created'
                        : step.kind === 'encrypt'
                        ? 'Wallet locked'
                        : 'Passkey verified',
                )
                if (result.next) {
                    rememberPasskeyStep(result.next)
                } else {
                    rememberPasskeyStep(null)
                    await refreshStatus()
                }
            })
            .catch(caught => {
                setError(walletPasskeyErrorMessage(caught))
                void preparePasskey()
            })
            .finally(() => setBusy(false))
    }

    if (!status || !stage || !remaining) {
        return (
            <Screen>
                <div className={styles.body}>
                    <h1 className={styles.title}>
                        {setupError ? 'Your wallet isn’t ready yet' : 'Making your wallet'}
                    </h1>
                    <p className={styles.text}>
                        {setupError || 'Creating your keys on this device. This takes a moment.'}
                    </p>
                </div>
                <div className={styles.actions}>
                    <button
                        type="button"
                        className={styles.primary}
                        onClick={onRetrySetup}
                        disabled={!setupError}
                    >
                        {setupError ? 'Try again' : 'Making your wallet…'}
                    </button>
                </div>
            </Screen>
        )
    }

    const unearnedSats = status.passkey_verified ? 0 : stepSats.passkey

    if (skipping) {
        return (
            <Screen>
                <div className={styles.topBar} />
                <div className={styles.body}>
                    <h1 className={styles.title}>Skip the passkey?</h1>
                    <p className={styles.text}>
                        Your wallet file can still bring your wallet back. Without a passkey, keep
                        that file safe.
                    </p>
                    {unearnedSats > 0 && (
                        <span className={styles.chip}>
                            {compactSats(unearnedSats)} sats waiting
                        </span>
                    )}
                </div>
                <div className={styles.actions}>
                    <button
                        type="button"
                        className={styles.primary}
                        onClick={() => setSkipping(false)}
                    >
                        Add the passkey
                    </button>
                    <button
                        type="button"
                        className={styles.quiet}
                        onClick={() => onClose({ askForHelloPost: true })}
                    >
                        Not now
                    </button>
                </div>
            </Screen>
        )
    }

    const stages: Stage[] = passkeysAvailable ? ['file', 'passkey', 'gift'] : ['file', 'gift']
    const header = (
        <StepHeader
            index={stages.indexOf(stage) + 1}
            total={stages.length}
            name={STAGE_NAMES[stage]}
            // The wallet file is required; only the passkey can wait.
            onLater={stage === 'passkey' ? () => setSkipping(true) : undefined}
        />
    )

    if (stage === 'file') {
        return (
            <Screen>
                {header}
                <div className={styles.body}>
                    {/* Two statements at title weight, well apart; the reward sits in the
                        same orange panel as the passkey step's, under the ask. */}
                    <div className={styles.downloadStatements}>
                        <h1 className={styles.title}>
                            We created a <span className={styles.titleAmount}>wallet</span> for you.
                        </h1>
                        <div className={styles.downloadAsk}>
                            <h2 className={styles.title}>
                                <span className={styles.titleAmount}>Back up</span> your wallet
                                {stepSats.file > 0 ? '' : '.'}
                            </h2>
                            {stepSats.file > 0 && (
                                <div className={styles.reward}>
                                    <span className={styles.rewardLabel}>You receive</span>
                                    <span className={styles.rewardAmount}>
                                        <Zap size={22} fill="currentColor" aria-hidden="true" />
                                        {formatSats(stepSats.file)} sats
                                    </span>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
                <Actions error={error}>
                    <button
                        type="button"
                        className={cn(styles.primary, styles.primaryMoney)}
                        onClick={downloadFile}
                        disabled={busy}
                        data-testid="wallet-setup-download"
                    >
                        {busy ? 'Saving…' : 'Accept Wallet'}
                    </button>
                    <p className={styles.footnote}>Keep it private.</p>
                </Actions>
            </Screen>
        )
    }

    if (stage === 'passkey') {
        const copy = PASSKEY_COPY[passkeyKind]
        // Two approvals: create, then lock (which also verifies). An older half-finished
        // setup can still land on a separate verify, counted as the second.
        const prompt = { create: 1, encrypt: 2, verify: 2 }[passkeyKind]
        return (
            <Screen>
                {header}
                <div className={styles.body}>
                    {/* Stays until the passkey is made: a toast would fire under iOS Safari's
                        "Do you want to download" prompt and be gone before it closes. */}
                    {passkeyKind === 'create' && status.backup_file_saved && (
                        <p className={styles.savedLine} role="status">
                            <Check size={16} strokeWidth={3} aria-hidden="true" />
                            Wallet file saved
                        </p>
                    )}
                    {/* The download screen's shape: statements at title weight, the sats in orange. */}
                    <h1 className={cn(styles.title, styles.statements)}>
                        {passkeyKind === 'create' ? (
                            // Names the two approvals ahead; the reward sits in its panel.
                            <>
                                <span>
                                    <span className={styles.titleAmount}>Create</span> a passkey to
                                    automate wallet recovery.
                                </span>
                            </>
                        ) : (
                            <>
                                <span>
                                    <span className={styles.titleAmount}>{copy.verb}</span>{' '}
                                    {copy.title}
                                </span>
                            </>
                        )}
                    </h1>
                    {/* The reward sits apart from the ask, so it reads as what you get. */}
                    {stepSats.passkey > 0 && (
                        <div className={cn(styles.reward, styles.rewardSpaced)}>
                            <span className={styles.rewardLabel}>
                                {passkeyKind === 'create' ? 'You receive' : 'Receive now'}
                            </span>
                            <span className={styles.rewardAmount}>
                                <Zap size={22} fill="currentColor" aria-hidden="true" />
                                {formatSats(stepSats.passkey)} sats
                            </span>
                        </div>
                    )}
                    <span className={styles.approvalDots} aria-hidden="true">
                        {[1, 2].map(dot => (
                            <span
                                key={dot}
                                className={cn(
                                    styles.approvalDot,
                                    dot === prompt && styles.approvalDotCurrent,
                                )}
                            />
                        ))}
                    </span>
                </div>
                <Actions error={error}>
                    <button
                        type="button"
                        className={cn(styles.primary, styles.primaryMoney)}
                        onClick={!passkeyStep && error ? retryPreparePasskey : advancePasskey}
                        disabled={busy || (!passkeyStep && !error)}
                        data-testid="wallet-setup-passkey"
                    >
                        {busy
                            ? 'Waiting for your passkey…'
                            : !passkeyStep
                            ? error
                                ? 'Try again'
                                : 'Getting ready…'
                            : copy.cta}
                    </button>
                    <p className={styles.footnote}>
                        {passkeyKind === 'create'
                            ? 'We will use it to lock your wallet'
                            : 'For automatic wallet recovery'}
                    </p>
                    {/* A phone without a screen lock, or a passkey app that can't hold the
                        wallet's key, can't finish this step: offer the way past it here. */}
                    {error && !busy && (
                        <button
                            type="button"
                            className={styles.secondary}
                            onClick={() => setSkipping(true)}
                            data-testid="wallet-setup-passkey-skip"
                        >
                            Skip the passkey
                        </button>
                    )}
                    {/* A passkey made earlier that won't approve (removed from the password
                        manager, another device): set it aside and make a new one. */}
                    {error && !busy && passkeyKind !== 'create' && (
                        <button
                            type="button"
                            className={styles.quiet}
                            onClick={() => void startNewPasskey()}
                            data-testid="wallet-setup-passkey-new"
                        >
                            Use a new passkey
                        </button>
                    )}
                </Actions>
            </Screen>
        )
    }

    const gifts = signupGifts
    const sending = gifts.some(gift => ['pending', 'sending'].includes(gift.status))
    const sendingSats = gifts
        .filter(gift => ['pending', 'sending'].includes(gift.status))
        .reduce((total, gift) => total + gift.amount_sats, 0)
    const failed = gifts.some(gift => gift.status === 'failed')
    const allSent =
        gifts.length > 0 && gifts.every(gift => gift.status === 'sent') && !remaining.giftUnclaimed
    // A claim that didn't start can be tried again.
    const retryable = !!giftError && (remaining.giftUnclaimed || sending)
    const retryGift = () => {
        setGiftError('')
        setGiftAttempt(attempt => attempt + 1)
    }
    return (
        <Screen celebrate={allSent}>
            {header}
            <div className={styles.body}>
                {sending && !failed && !retryable && sendingSats > 0 && (
                    <span className={styles.sending} role="status">
                        <span className={styles.spinner} aria-hidden="true" />
                        <span>
                            Sending{' '}
                            <span className={styles.sendingAmount}>
                                <Zap size={14} fill="currentColor" aria-hidden="true" />
                                {formatSats(sendingSats)}
                            </span>{' '}
                            sats…
                        </span>
                    </span>
                )}
                <h1 className={styles.title}>
                    {totalSats <= 0 ? (
                        'Your wallet is backed up'
                    ) : allSent ? (
                        // The amount appears once, in the sentence.
                        <>
                            You&apos;ve earned{' '}
                            <span className={cn(styles.titleAmount, styles.titleMoney)}>
                                <Zap
                                    className={styles.titleBolt}
                                    fill="currentColor"
                                    aria-hidden="true"
                                />
                                {formatSats(receivedSats)} satoshis
                            </span>
                            !
                        </>
                    ) : failed || retryable ? (
                        'Your wallet is backed up'
                    ) : (
                        'Sending your sats'
                    )}
                </h1>
                <p className={styles.text}>
                    {totalSats <= 0 ? (
                        'You can sign in and get it back on any new phone with your passkey or wallet file.'
                    ) : allSent ? (
                        <>
                            You can use them to <span className={styles.titleAmount}>Upvalue</span>{' '}
                            content you like to reward the author.
                        </>
                    ) : retryable ? (
                        `${giftError} Your wallet is safe; only the gift is waiting.`
                    ) : failed ? (
                        'Part of the gift could not be sent. We will look into it.'
                    ) : (
                        'Your wallet is backed up. The sats are on their way.'
                    )}
                </p>
            </div>
            <div className={styles.actions}>
                {retryable ? (
                    <button type="button" className={styles.primary} onClick={retryGift}>
                        Try again
                    </button>
                ) : (
                    <button
                        type="button"
                        className={styles.primary}
                        onClick={() => onClose({ askForHelloPost: false, introduce: true })}
                        data-testid="wallet-setup-finish"
                    >
                        Introduce yourself
                    </button>
                )}
                {retryable ? (
                    <button
                        type="button"
                        className={styles.quiet}
                        onClick={() => onClose({ askForHelloPost: true })}
                    >
                        Later
                    </button>
                ) : (
                    <button
                        type="button"
                        className={styles.secondary}
                        onClick={() => onClose({ askForHelloPost: false })}
                        data-testid="wallet-setup-skip-intro"
                    >
                        Skip
                    </button>
                )}
            </div>
        </Screen>
    )
}

function Screen({
    children,
    celebrate = false,
}: {
    children: React.ReactNode
    celebrate?: boolean
}) {
    const { toast } = useContext(StepToastContext)
    return (
        <div className={styles.overlay} role="dialog" aria-modal="true" aria-label="Wallet setup">
            {toast && (
                <div key={toast.id} className={styles.stepToast} role="status">
                    <Check size={18} strokeWidth={3} aria-hidden="true" />
                    {toast.text}
                </div>
            )}
            <div className={cn(styles.screen, celebrate && styles.celebrate)}>{children}</div>
        </div>
    )
}

function Actions({ error, children }: { error: string; children: React.ReactNode }) {
    return (
        <div className={styles.actions}>
            {error && (
                <p className={styles.error} role="alert">
                    {error}
                </p>
            )}
            {children}
        </div>
    )
}

// "1/3 · DOWNLOAD WALLET" and a segmented bar: the same header on every screen.
export function StepHeader({
    index,
    total,
    name,
    onLater,
    laterLabel = 'Later',
}: {
    index?: number
    total?: number
    name: string
    onLater?: () => void
    laterLabel?: string
}) {
    const counted = !!index && !!total
    return (
        <div className={styles.header}>
            <div className={styles.topBar}>
                <span className={styles.step}>
                    {counted && (
                        <span className={styles.stepCount}>
                            {index}/{total}
                        </span>
                    )}
                    {name}
                </span>
                {onLater && (
                    <button type="button" className={styles.later} onClick={onLater}>
                        {laterLabel}
                    </button>
                )}
            </div>
            {counted && (
                <div className={styles.progress} aria-hidden="true">
                    {Array.from({ length: total }, (_, segment) => (
                        <span
                            key={segment}
                            className={cn(styles.segment, segment < index && styles.segmentDone)}
                        />
                    ))}
                </div>
            )}
        </div>
    )
}

// The reward, in one line right under the button.
export function Reward({ sats, note }: { sats: number; note?: string }) {
    if (sats <= 0 && !note) return <span className={styles.foot} />
    return (
        <span className={styles.reward}>
            {sats > 0 && `+${compactSats(sats)} sats`}
            {sats > 0 && note && ' · '}
            {note && <span className={styles.rewardNote}>{note}</span>}
        </span>
    )
}
